<script setup lang="ts">import { toRef as _toRef, computed as _computed } from 'vue';

import { faClockRotateLeft, faLocationArrow } from '@fortawesome/pro-light-svg-icons'
// Custom images
import Result from './Result.vue'
import { backgroundFor, defaultIconFor, genericPlaceImage, hintFor, textFor } from './utils'
import LoadingSkeleton from './LoadingSkeleton.vue'
import { useOmniSearch, useQueryStore } from './useOmniSearch'
import Tip from './Tip.vue'
import happyHolidays from './holidays.png'

const __$temp_1 = (useOmniSearch()),
  emptyState = _toRef(__$temp_1, 'emptyState'),
  popularNearby = _toRef(__$temp_1, 'popularNearby');
const queryStore = useQueryStore()

const popularSuggestions = [
  { text: 'Any Place' },
  emptyState.value?.holidaySeason
    ? { text: 'Happy Holidays', imageUrl: happyHolidays }
    : { text: 'Any Store' },
]

const tip = _computed(() => {
  if (emptyState.value?.holidaySeason)
    return 'Try searching for ’Christmas gifts’, ‘restaurants in SF’, or ‘Target’'
  else
    return 'Try searching for ‘coffee gifts in SF’, ‘spa day’, or ‘Target’'
})
</script>

<template>
  <LoadingSkeleton v-if="!emptyState"/>
  <div v-else class="flex flex-col gap-4 text-sm">
    <div v-if="queryStore.all.length" class="flex flex-wrap flex-col">
      <div class="text-v2-secondary mb-4 inline space-x-2">
        <span>Recent searches</span>
        <GButton label="Clear" flat primary @click="queryStore.clear"/>
      </div>
      <Result
        v-for="query in queryStore.all"
        :key="`stored-query-${query}`"
        :value="{ recentSearch: { query }}"
        :text="query"
        :icon="faClockRotateLeft"
      />
    </div>
    <div v-else class="flex flex-wrap gap-2">
      <Tip :text="tip"/>

      <Result
        text="Nearby popular gifts"
        :icon="faLocationArrow"
        :value="popularNearby"
        withBorder
      />

      <Result
        v-for="landingPage in emptyState?.landingPages"
        :key="landingPage.id"
        :value="landingPage"
        :imageUrl="backgroundFor(landingPage, emptyState.city)"
        :text="textFor(landingPage)"
        :icon="defaultIconFor(landingPage)"
        :hint="hintFor(landingPage)"
        :giftlyMetadata="landingPage.giftlyMetadata"
        withBorder
      />

      <Result
        v-for="suggestion in emptyState?.categories"
        :key="suggestion.id"
        :value="suggestion"
        :imageUrl="backgroundFor(suggestion, emptyState.city)"
        :text="textFor(suggestion)"
        :icon="defaultIconFor(suggestion)"
        :hint="hintFor(suggestion)"
        :giftlyMetadata="suggestion.giftlyMetadata"
        withBorder
      />

      <Result
        v-for="suggestion in emptyState?.placesAndMultiplaces"
        :key="suggestion.id"
        :value="suggestion"
        :imageUrl="backgroundFor(suggestion)"
        :text="textFor(suggestion)"
        :icon="defaultIconFor(suggestion)"
        :hint="hintFor(suggestion)"
        :giftlyMetadata="suggestion.giftlyMetadata"
        withBorder
      />
    </div>

    <div v-if="emptyState?.occasions?.length" class="flex flex-col gap-3">
      <span class="text-v2-secondary">Browse gift ideas</span>
      <div class="flex flex-wrap gap-2">
        <Result
          v-for="suggestion in emptyState?.occasions"
          :key="suggestion.id"
          :value="suggestion"
          :imageUrl="backgroundFor(suggestion)"
          :text="textFor(suggestion)"
          :icon="defaultIconFor(suggestion)"
          :hint="hintFor(suggestion)"
          withBorder
        />
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <span class="text-v2-secondary">Popular</span>
      <div class="flex flex-wrap gap-2">
        <Result
          v-for="suggestion in popularSuggestions"
          :key="suggestion.text"
          :value="{ generic: { tagline: suggestion.text }}"
          v-bind="suggestion"
          hint="Works anywhere"
          withBorder
          class="flex-grow"
        >
          <template #image>
            <img
              :src="suggestion.imageUrl || genericPlaceImage"
              :alt="suggestion.text"
            >
          </template>
        </Result>
      </div>
    </div>
  </div>
</template>
